import { Injectable, computed, inject, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable, catchError, finalize, map, switchMap, tap, throwError } from 'rxjs';
import { RuntimeConfigService } from '../../core/config/runtime-config.service';
import { AuthService } from '../../core/auth/auth.service';
import {
  BackofficeCompanyUpsertPayload,
  BackofficePlanFeatureDefinition,
  BackofficePlanUpsertPayload,
  BackofficeUserMutationResponse,
  BackofficeOverview,
  BackofficeUserUpsertPayload
} from './backoffice.models';

const PLAN_FEATURE_OVERRIDES: BackofficePlanFeatureDefinition[] = [
  {
    key: 'challengeRequirement',
    label: 'Challenge requirements',
    description: 'Challenge functional requirements before scenario generation or audit.'
  },
  {
    key: 'auditFeature',
    label: 'Audit feature',
    description: 'Assess one feature for QA quality, coverage and explainable risk scoring.'
  },
  {
    key: 'review',
    label: 'Review',
    description: 'Run manual merge request QA review and generate a focused review report.'
  },
  {
    key: 'agenticWorkflow',
    label: 'Auto review',
    description: 'Run automatic CI/CD review from connected merge request workflows.'
  }
];

const PLAN_FEATURE_ORDER = [
  'challengeRequirement',
  'improveScenario',
  'generateScenario',
  'implementScenario',
  'auditFeature',
  'review',
  'agenticWorkflow'
];

function normalizePlanFeatureCatalog(features: BackofficePlanFeatureDefinition[]): BackofficePlanFeatureDefinition[] {
  const safeFeatures = Array.isArray(features) ? features.filter(Boolean) : [];
  const overrideByKey = new Map(PLAN_FEATURE_OVERRIDES.map((feature) => [feature.key, feature]));
  const featureByKey = new Map(
    safeFeatures.map((feature) => {
      const override = overrideByKey.get(feature.key);
      return [feature.key, override ? { ...feature, ...override } : feature];
    })
  );

  PLAN_FEATURE_OVERRIDES.forEach((feature) => {
    if (!featureByKey.has(feature.key)) {
      featureByKey.set(feature.key, feature);
    }
  });

  return [...featureByKey.values()].sort((left, right) => {
    const leftIndex = PLAN_FEATURE_ORDER.indexOf(left.key);
    const rightIndex = PLAN_FEATURE_ORDER.indexOf(right.key);
    const normalizedLeftIndex = leftIndex === -1 ? Number.MAX_SAFE_INTEGER : leftIndex;
    const normalizedRightIndex = rightIndex === -1 ? Number.MAX_SAFE_INTEGER : rightIndex;
    return normalizedLeftIndex - normalizedRightIndex || left.label.localeCompare(right.label);
  });
}

@Injectable({ providedIn: 'root' })
export class BackofficeService {
  private readonly http = inject(HttpClient);
  private readonly runtimeConfig = inject(RuntimeConfigService);
  private readonly authService = inject(AuthService);

  private readonly overviewState = signal<BackofficeOverview | null>(null);
  private readonly loadingState = signal(false);
  private readonly errorState = signal<string | null>(null);

  readonly overview = computed(() => this.overviewState());
  readonly loading = computed(() => this.loadingState());
  readonly error = computed(() => this.errorState());
  readonly users = computed(() => this.overviewState()?.users ?? []);
  readonly companies = computed(() => this.overviewState()?.companies ?? []);
  readonly planOptions = computed(() => this.overviewState()?.plans ?? []);
  readonly planFeatureCatalog = computed(() =>
    normalizePlanFeatureCatalog(this.overviewState()?.planFeatureCatalog ?? [])
  );
  readonly productAccessOptions = computed(() => this.overviewState()?.productAccessOptions ?? []);
  readonly roleOptions = computed(() => this.overviewState()?.roles ?? []);
  readonly alerts = computed(() => this.overviewState()?.alerts ?? []);
  readonly metrics = computed(() => this.overviewState()?.metrics ?? null);
  readonly scope = computed(() => this.overviewState()?.scope ?? null);
  readonly currentCompanyId = computed(
    () => this.overviewState()?.currentCompanyId ?? this.authService.user()?.company?.id ?? this.authService.user()?.companyId ?? null
  );
  readonly userCanManageCompanies = computed(() => this.authService.user()?.role === 'superadmin');

  private get apiBaseUrl(): string {
    return `${this.runtimeConfig.getConfig().backendBaseUrl}/api/backoffice`;
  }

  loadOverview(force = false): void {
    if (this.loadingState() && !force) {
      return;
    }

    this.refreshOverview().subscribe({
      error: () => {
        // Error state is already captured for the UI.
      }
    });
  }

  refreshOverview(): Observable<BackofficeOverview> {
    this.loadingState.set(true);
    this.errorState.set(null);

    return this.http.get<BackofficeOverview>(`${this.apiBaseUrl}/overview`).pipe(
      tap((overview) => this.overviewState.set(overview)),
      catchError((error) => {
        this.errorState.set('Impossible de charger le backoffice.');
        return throwError(() => error);
      }),
      finalize(() => this.loadingState.set(false))
    );
  }

  saveUser(payload: BackofficeUserUpsertPayload): Observable<BackofficeUserMutationResponse> {
    return this.http.post(`${this.apiBaseUrl}/users/upsert`, payload).pipe(
      switchMap((result) => this.refreshOverview().pipe(
        map((overview) => ({
          overview,
          result: result as BackofficeUserMutationResponse['result']
        }))
      ))
    );
  }

  deleteUser(userId: string): Observable<BackofficeOverview> {
    return this.http.post(`${this.apiBaseUrl}/users/${userId}/delete`, {}).pipe(
      switchMap(() => this.refreshOverview())
    );
  }

  resendInvitation(userId: string): Observable<BackofficeUserMutationResponse> {
    return this.http.post(`${this.apiBaseUrl}/users/${userId}/resend-invitation`, {}).pipe(
      switchMap((result) => this.refreshOverview().pipe(
        map((overview) => ({
          overview,
          result: result as BackofficeUserMutationResponse['result']
        }))
      ))
    );
  }

  sendPasswordReset(userId: string): Observable<BackofficeUserMutationResponse> {
    return this.http.post(`${this.apiBaseUrl}/users/${userId}/send-password-reset`, {}).pipe(
      switchMap((result) => this.refreshOverview().pipe(
        map((overview) => ({
          overview,
          result: result as BackofficeUserMutationResponse['result']
        }))
      ))
    );
  }

  saveCompany(payload: BackofficeCompanyUpsertPayload): Observable<BackofficeOverview> {
    return this.http.post(`${this.apiBaseUrl}/companies/upsert`, payload).pipe(
      switchMap(() => this.refreshOverview())
    );
  }

  deleteCompany(companyId: string): Observable<BackofficeOverview> {
    return this.http.post(`${this.apiBaseUrl}/companies/${companyId}/delete`, {}).pipe(
      switchMap(() => this.refreshOverview())
    );
  }

  savePlan(payload: BackofficePlanUpsertPayload): Observable<BackofficeOverview> {
    return this.http.post(`${this.apiBaseUrl}/plans/upsert`, payload).pipe(
      switchMap(() => this.refreshOverview())
    );
  }

  deletePlan(planKey: string): Observable<BackofficeOverview> {
    return this.http.post(`${this.apiBaseUrl}/plans/${encodeURIComponent(planKey)}/delete`, {}).pipe(
      switchMap(() => this.refreshOverview())
    );
  }
}
